/**
 * Pure geometry for client-side per-station fusion (workers/CONTRACTS.md
 * "Fusion: application-layer — DECISION 2026-08-10: no server mosaic
 * product"). There is no shared grid anymore — each station COG carries its
 * own real EPSG:3857 bbox (read off its own IFD via geotiff.js,
 * image.getBoundingBox(), same as the single-station path always did); the
 * "fusion window" is whatever the current MapLibre viewport shows. All math
 * here stays in EPSG:3857 meters (not normalized/window-relative) so
 * real-world distance comparisons (nearest-station-wins) are correct
 * regardless of the window's aspect ratio or canvas resolution.
 */
import { lonLatToMercator, WEB_MERCATOR_R } from "./geo";

export interface Bounds3857 {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface LonLatBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export function lonLatBoundsToMercator(b: LonLatBounds): Bounds3857 {
  const [minX, minY] = lonLatToMercator(b.west, b.south);
  const [maxX, maxY] = lonLatToMercator(b.east, b.north);
  return { minX, minY, maxX, maxY };
}

export function boundsIntersect(a: Bounds3857, b: Bounds3857): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
}

/** IFD index -> downsample factor: 0=full res, 1=2x overview, 2=4x overview
 * (proto/rust/bake-core/src/cog.rs writes exactly these two internal
 * overviews into every station COG — verified via `gdalinfo` against both
 * proto/bake/data/ and the live dev fixtures: overviews [2, 4] on every
 * station frame). */
export type OverviewLevel = 0 | 1 | 2;

/**
 * Overview level to DECODE, by zoom alone — no separate "wide" vs
 * "regional" MODE anymore (workers/CONTRACTS.md: fusion renders at every
 * zoom; a station's footprint naturally comes to dominate the viewport as
 * you zoom into it, converging toward what used to be a distinct composite
 * view). Deliberately a single-argument, station-agnostic function: the
 * overview level is a per-VIEW decision (every caller in fusionController.ts
 * computes it once per zoom and applies the same value to every station slot
 * that frame) — there is no "footprint dominance"-style per-station input
 * here, on purpose, because there used to be one (a heuristic assuming a
 * single station's disc fills the viewport) and it produced visibly
 * mismatched grids in overlap regions where several stations' footprints
 * share the screen at once (KAMX/KTBW/KMLB, ~z8) — see
 * fusionController.test.ts / the fix's before/after screenshots.
 *
 * The threshold picked here isn't the zoom itself — it's how big one native
 * (level-0) grid cell renders ON SCREEN, compared against the SAME
 * WebMercatorQuad-z7 pixel lattice every station COG now shares post grid-
 * migration (proto/rust/bake-core/src/grid.rs's `Z7_PIXEL_M`,
 * ~1222.9924525628 m — reproduced here rather than imported since this is a
 * client bundle with no path to the Rust crate; `Z7_PIXEL_M` below must stay
 * numerically identical to grid.rs's constant). Comparing REAL on-screen
 * cell size (not just an arbitrary zoom number) is what keeps the level
 * transition small and unobtrusive: right at each threshold the coarser
 * level's texel is at most ~2-3 screen px, not the ~8px block the old z<8
 * cutoff let level-1 grow to just before snapping to full res — a jump big
 * enough that the transient mismatch during a station's in-flight re-decode
 * (acceptable on its own, see fusionController.ts's doc comment) read as
 * grossly misaligned grids instead of a barely-noticeable blend.
 *
 * `metersPerScreenPixel` mirrors MapLibre GL JS's own zoom->world-size
 * mapping exactly: `Transform`'s `worldSize = tileSize * 2**zoom` with a
 * fixed `tileSize = 512` (verified against
 * node_modules/maplibre-gl/src/geo/transform_helper.ts's
 * `this._tileSize = 512; // constant` — NOT the raster source's own tile
 * size, which is irrelevant to screen-pixel scale). This is a PROJECTED
 * (EPSG:3857) meters-per-pixel, latitude-independent by construction (the
 * whole point of a Mercator tile pyramid) — deliberately not the popular
 * "ground meters per pixel" formula (which has a cos(lat) factor): the
 * quantity being compared, `Z7_PIXEL_M`, is itself a projected-meters
 * pixel size, so the comparison must stay in the same (projected) units on
 * both sides.
 *
 * This is a decode/GPU-upload-size choice, NOT a network-bytes one:
 * fusionDecodeWorker.ts fetches each station's COG whole regardless of
 * level (measured — windowed HTTP Range reads don't win for files this
 * small, see that file's doc comment) and decodes the level chosen here
 * from the in-memory buffer, so a coarser level still means less data to
 * copy into a Uint8Array and upload as a GPU texture, and a smaller texture
 * for the shader to sample — just not fewer bytes over the wire.
 */
const WORLD_HALF_EXTENT_M = WEB_MERCATOR_R * Math.PI;
/** MapLibre GL JS's fixed internal tile size for its zoom<->worldSize
 * mapping (see doc comment above) — distinct from any raster source's own
 * tileSize, which plays no part in screen-pixel scale. */
const MAPLIBRE_WORLD_TILE_PX = 512;
/** Mirrors proto/rust/bake-core/src/grid.rs's `Z7_PIXEL_M` (256px COG tiles,
 * WebMercatorQuad z7): `2 * WORLD_HALF_EXTENT_M / (256 * 2**7)` ==
 * ~1222.9924525628 m/px, identical for every station post grid-migration. */
const Z7_PIXEL_M = (2 * WORLD_HALF_EXTENT_M) / (256 * 2 ** 7);
/** Thresholds on one z7 (level-0) cell's on-screen size, in CSS/device
 * pixels — tuned so the coarser level's own texel (2x or 4x that cell) is
 * never much more than ~2-3 screen px right at the moment it switches to
 * the next-finer level (see the "This is a decode..." doc above). */
const LEVEL2_MAX_CELL_SCREEN_PX = 0.5;
const LEVEL1_MAX_CELL_SCREEN_PX = 1.5;

function metersPerScreenPixel(zoom: number): number {
  return (2 * WORLD_HALF_EXTENT_M) / (MAPLIBRE_WORLD_TILE_PX * 2 ** zoom);
}

export function overviewLevelForZoom(zoom: number): OverviewLevel {
  const cellScreenPx = Z7_PIXEL_M / metersPerScreenPixel(zoom);
  if (cellScreenPx < LEVEL2_MAX_CELL_SCREEN_PX) return 2;
  if (cellScreenPx < LEVEL1_MAX_CELL_SCREEN_PX) return 1;
  return 0;
}

export interface UvTransform {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Affine transform mapping a normalized fusion-window UV ((0,0)=NW corner,
 * (1,1)=SE corner, matching how the compositor's fullscreen quad is
 * parameterized) into a station's own normalized texture UV (same
 * convention — row 0/v=0 is the raster's north edge, per geotiff/TIFF
 * row-major-from-top convention, matching geo.ts's existing corner
 * ordering). Derivation: worldX = winMinX + u*(winMaxX-winMinX), and
 * uv_station.x = (worldX - stMinX)/(stMaxX-stMinX); substituting gives the
 * scale/offset below directly (both are affine in u, so composing them
 * stays affine — no per-pixel work needed on the CPU, just this once per
 * station per render).
 *
 * Returns null if the station's bbox doesn't intersect the window at all —
 * nothing for the shader to sample, so the caller can skip binding it
 * (or mark that texture slot inactive).
 */
export function stationUvTransform(window: Bounds3857, station: Bounds3857): UvTransform | null {
  if (!boundsIntersect(window, station)) return null;
  const winW = window.maxX - window.minX;
  const winH = window.maxY - window.minY;
  const stW = station.maxX - station.minX;
  const stH = station.maxY - station.minY;
  if (winW <= 0 || winH <= 0 || stW <= 0 || stH <= 0) return null;

  return {
    scaleX: winW / stW,
    offsetX: (window.minX - station.minX) / stW,
    scaleY: winH / stH,
    offsetY: (station.maxY - window.maxY) / stH,
  };
}

/** Real EPSG:3857 world position for a normalized fusion-window UV — used
 * (mirroring stationUvTransform's derivation) for the shader's real-world
 * nearest-station distance calc, so comparisons are correct regardless of
 * the window's aspect ratio or the canvas's pixel resolution. */
export function worldPositionForUv(window: Bounds3857, u: number, v: number): [number, number] {
  const x = window.minX + u * (window.maxX - window.minX);
  const y = window.maxY - v * (window.maxY - window.minY);
  return [x, y];
}
